import { FC, lazy, ReactElement } from "react";
import {
  createHashRouter,
  redirect,
  RouteObject,
  RouterProvider,
} from "react-router";
import RouteErrorPage from "./RouteErrorPage";
import NotFoundPage from "app/pages/public/errors/NotFoundPage";
import browserPaths from "details/navigation/browser/browserPaths";
import authGuard from "core/authUser/auth/guards/authGuard/authGuard";
import PublicNavigationLocations from "core/navigation/PublicNavigationLocations";
import MapBrowserPath from "details/navigation/browser/mappers/mapBrowserPath/MapBrowserPath";
import DashboardNavigationLocations from "core/navigation/DashboardNavigationLocations";
import guestGuard from "core/authUser/auth/guards/guestGuard/guestGuard";
import firstLoginResetPasswordLoader from "core/authUser/auth/guards/firstLoginResetPasswordLoader/firstLoginResetPasswordLoader";
import { getDefaultDashboardPath } from "core/authUser/auth/services/RoleDashboardService";
import {
  getDashboardRouteFeatureKeys,
  getDashboardRouteRoleLabels,
  isKybModuleGatedRoute,
} from "@/app/shared/permissions/dashboardRoutePermissions";
import {
  assertDashboardRouteAccess,
  assertDashboardRouteRole,
  assertDashboardModuleAccess,
} from "@/app/routes/routePermissionGuard";
import { useKybModuleStore } from "@/core/stores/administration/kybModuleStore";
import { pick } from "@/utils/lookup";

/* istanbul ignore next */
const PublicLayout = lazy(() => import("app/pages/public/layout/PublicLayout"));
/* istanbul ignore next */
const PublicErrorLayout = lazy(
  () => import("app/pages/public/errors/layout/PublicErrorLayout"),
);
/* istanbul ignore next */
const DashboardLayout = lazy(
  () => import("app/pages/dashboard/layout/DashboardLayout"),
);
/* istanbul ignore next */
const DashboardErrorLayout = lazy(
  () => import("app/pages/dashboard/errors/layout/DashboardErrorLayout"),
);

//Public pages components
/* istanbul ignore next */
const HomePage = lazy(() => import("app/pages/public/pages/homePage/HomePage"));
/* istanbul ignore next */
const FirstLoginResetPasswordPage = lazy(
  () =>
    import("app/pages/public/pages/firstLoginResetPasswordPage/FirstLoginResetPasswordPage"),
);

//Dashboard pages components
/* istanbul ignore next */
const DashboardPage = lazy(
  () => import("app/pages/dashboard/pages/dashboardPage/DashboardPage"),
);
// const CreateAccountPage = lazy(
//   () => import("app/pages/dashboard/pages/createAccountPage/CreateAccountPage")
// );
/* istanbul ignore next */
const ProspectsPage = lazy(
  () => import("@/app/pages/dashboard/pages/customers/prospects/ProspectsPage"),
);
/* istanbul ignore next */
const ClientsPage = lazy(
  () => import("@/app/pages/dashboard/pages/customers/client360/ClientsPage"),
);
/* istanbul ignore next */
const ProfilPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/customers/fiche-client360/FicheClient360"),
);
/* istanbul ignore next */
const AdministrationPage = lazy(
  () =>
    import("app/pages/dashboard/pages/administrationPage/AdministrationPage"),
);
/* istanbul ignore next */
const RiskEntityReferentialPage = lazy(
  () =>
    import("app/pages/dashboard/pages/administrationPage/RiskEntityReferentialPage"),
);
/* istanbul ignore next */
const PlatformSettingsPage = lazy(
  () =>
    import("app/pages/dashboard/pages/administrationPage/PlatformSettingsPage"),
);
/* istanbul ignore next */
const AgencesPage = lazy(
  () => import("app/pages/dashboard/pages/administrationPage/AgencesPage"),
);
/* istanbul ignore next */
const NouvelleDemandePage = lazy(
  () => import("app/pages/dashboard/pages/demandes/NouvelleDemandePage"),
);
/* istanbul ignore next */
const CreateProspectPage = lazy(
  () =>
    import("app/pages/dashboard/pages/demandes/create-prospect/CreateProspectPage"),
);
/* istanbul ignore next */
const CreateProspectPPPage = lazy(
  () =>
    import("app/pages/dashboard/pages/demandes/create-prospect/CreateProspectPPPage"),
);
/* istanbul ignore next */
const CreateProspectPMPage = lazy(
  () =>
    import("app/pages/dashboard/pages/demandes/create-prospect/CreateProspectPMPage"),
);
/* istanbul ignore next */
const OnboardingPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/demandes/onboarding/OnboardingPage"),
);
/* istanbul ignore next */
const PMOnboardingPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/demandes/pm-onboarding/PMOnboardingPage"),
);
/* istanbul ignore next */
const CommercialDashboardPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/commercialDashboard/CommercialDashboardPage"),
);
/* istanbul ignore next */
const AgenceDashboardPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/agenceDashboard/AgenceDashboardPage"),
);
/* istanbul ignore next */
const ComplianceDashboardPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/complianceDashboard/ComplianceDashboardPage"),
);
/* istanbul ignore next */
const ConformiteDashboardPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/conformiteDashboard/ConformiteDashboardPage"),
);
/* istanbul ignore next */
const L1ReviewPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/compliance/l1Review/L1ReviewPage"),
);
/* istanbul ignore next */
const P4ValidationPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/compliance/p4Validation/P4ValidationPage"),
);
/* istanbul ignore next */
const EddCasesPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/compliance/edd/EddCasesPage"),
);
/* istanbul ignore next */
const EddCaseDetailPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/compliance/edd/EddCaseDetailPage"),
);
/* istanbul ignore next */
const L1ReviewDetailPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/compliance/l1Review/L1ReviewDetailPage"),
);

/* istanbul ignore next */
const KybCaseDetailPage = lazy(
  () =>
    import("@/app/pages/dashboard/pages/compliance/kybCase/KybCaseDetailPage"),
);

/* istanbul ignore next */
const MandatesPage = lazy(
  () => import("@/app/pages/dashboard/pages/compliance/kybMandates/MandatesPage"),
);

/* istanbul ignore next */
const KybPerimeterPage = lazy(
  () => import("@/app/pages/dashboard/pages/compliance/kybPerimeter/KybPerimeterPage"),
);
const KybRetentionPage = lazy(
  () => import("@/app/pages/dashboard/pages/compliance/kybRetention/KybRetentionPage"),
);
const KybComplementTypesPage = lazy(
  () => import("@/app/pages/dashboard/pages/compliance/kybComplementTypes/KybComplementTypesPage"),
);
const KybMonitoringPage = lazy(
  () => import("@/app/pages/dashboard/pages/compliance/kybMonitoring/KybMonitoringPage"),
);
const AccessRecertificationPage = lazy(
  () => import("@/app/pages/dashboard/pages/compliance/kybAccessRecertification/AccessRecertificationPage"),
);
const CorporateAccessPage = lazy(
  () => import("@/app/pages/dashboard/pages/compliance/kybCorporateAccess/CorporateAccessPage"),
);

/* istanbul ignore next */
const CorporateFirstLoginPage = lazy(
  () => import("app/pages/public/pages/corporateFirstLoginPage/CorporateFirstLoginPage"),
);

/* istanbul ignore next */
const SignatureGridsPage = lazy(
  () => import("@/app/pages/dashboard/pages/compliance/kybSignatureGrids/SignatureGridsPage"),
);

// DEV ONLY: Manual testing of error boundaries - remove before production
/* istanbul ignore next */
const TestCrashPage = lazy(() => import("app/pages/public/TestCrashPage"));
/* istanbul ignore next */
const DashboardPermissionDeniedPage = lazy(
  () => import("@/app/routes/DashboardPermissionDeniedPage"),
);
/* istanbul ignore next */
const ModuleInactivePage = lazy(() => import("@/app/routes/ModuleInactivePage"));

const Router: FC = () => {
  // Administration feature toggle:
  // - Enabled in non-production environments
  // - In production, the route remains protected even if the menu entry is visible but disabled
  // const isAdministrationEnabled = process.env.NODE_ENV !== "production";

  const mapPublicNavigationLocationToComponent = (
    publicNavigationLocations: PublicNavigationLocations,
  ) => {
    switch (publicNavigationLocations) {
      case PublicNavigationLocations.HOME:
        return <HomePage />;
      case PublicNavigationLocations.FIRST_LOGIN_RESET_PASSWORD:
        return <FirstLoginResetPasswordPage />;
      case PublicNavigationLocations.CORPORATE_FIRST_LOGIN:
        return <CorporateFirstLoginPage />;
      default: {
        const _exhaustive: never = publicNavigationLocations;
        return _exhaustive;
      }
    }
  };

  /** La page de chaque destination du tableau de bord. */
  const dashboardPages: Partial<Record<DashboardNavigationLocations, ReactElement>> = {
    [DashboardNavigationLocations.DASHBOARD]: <DashboardPage />,
    [DashboardNavigationLocations.COMMERCIAL_DASHBOARD]: <CommercialDashboardPage />,
    [DashboardNavigationLocations.AGENCE_DASHBOARD]: <AgenceDashboardPage />,
    [DashboardNavigationLocations.COMPLIANCE_DASHBOARD]: <ComplianceDashboardPage />,
    [DashboardNavigationLocations.CONFORMITE_DASHBOARD]: <ConformiteDashboardPage />,
    [DashboardNavigationLocations.PROSPECTS]: <ProspectsPage />,
    [DashboardNavigationLocations.CLIENT_360]: <ClientsPage />,
    [DashboardNavigationLocations.PROFIL]: <ProfilPage />,
    [DashboardNavigationLocations.ROLES_AND_RIGHTS]: <AdministrationPage />,
    [DashboardNavigationLocations.NOUVELLE_DEMANDE]: <NouvelleDemandePage />,
    [DashboardNavigationLocations.CREATE_PROSPECT]: <CreateProspectPage />,
    [DashboardNavigationLocations.CREATE_PROSPECT_PP]: <CreateProspectPPPage />,
    [DashboardNavigationLocations.CREATE_PROSPECT_PM]: <CreateProspectPMPage />,
    [DashboardNavigationLocations.ONBOARDING]: <OnboardingPage flowType="ONBOARDING" />,
    [DashboardNavigationLocations.KYB_CASE_DETAIL]: <KybCaseDetailPage />,
    [DashboardNavigationLocations.KYB_MANDATES]: <MandatesPage />,
    [DashboardNavigationLocations.KYB_CORPORATE_ACCESS]: <CorporateAccessPage />,
    [DashboardNavigationLocations.KYB_ACCESS_RECERTIFICATION]: <AccessRecertificationPage />,
    [DashboardNavigationLocations.KYB_MONITORING]: <KybMonitoringPage />,
    [DashboardNavigationLocations.KYB_COMPLEMENT_DOCUMENTS]: <KybComplementTypesPage />,
    [DashboardNavigationLocations.KYB_RETENTION]: <KybRetentionPage />,
    [DashboardNavigationLocations.KYB_PERIMETER]: <KybPerimeterPage />,
    [DashboardNavigationLocations.KYB_SIGNATURE_GRIDS]: <SignatureGridsPage />,
    [DashboardNavigationLocations.PM_ONBOARDING]: <PMOnboardingPage />,
    [DashboardNavigationLocations.IDENTITY_REPLACEMENT]: <OnboardingPage flowType="IDENTITY_REPLACEMENT" />,
    [DashboardNavigationLocations.RISK_ENTITY_REFERENTIAL]: <RiskEntityReferentialPage />,
    [DashboardNavigationLocations.PLATFORM_SETTINGS]: <PlatformSettingsPage />,
    [DashboardNavigationLocations.AGENCES]: <AgencesPage />,
    [DashboardNavigationLocations.COMPLIANCE_L1_REVIEW]: <L1ReviewPage />,
    [DashboardNavigationLocations.COMPLIANCE_P4_VALIDATION]: <P4ValidationPage />,
    [DashboardNavigationLocations.COMPLIANCE_EDD]: <EddCasesPage />,
    [DashboardNavigationLocations.COMPLIANCE_EDD_DETAIL]: <EddCaseDetailPage />,
    [DashboardNavigationLocations.COMPLIANCE_L1_REVIEW_DETAIL]: <L1ReviewDetailPage />,
  };

  const mapDashboardNavigationLocationToComponent = (
    dashboardNavigationLocations: DashboardNavigationLocations,
  ) => pick(dashboardPages, dashboardNavigationLocations);

  const publicRoutes: RouteObject[] = Object.keys(PublicNavigationLocations)
    .filter((key) => isNaN(Number(key)))
    .map((key) => {
      const location = key as PublicNavigationLocations;
      let loader: RouteObject["loader"];
      if (location === PublicNavigationLocations.HOME) {
        loader = () =>
          guestGuard() ? null : redirect(browserPaths.DASHBOARD.HOME);
      } else if (
        location === PublicNavigationLocations.FIRST_LOGIN_RESET_PASSWORD
      ) {
        loader = firstLoginResetPasswordLoader;
      }
      return {
        path: MapBrowserPath.mapToBrowserPath(location),
        element: mapPublicNavigationLocationToComponent(location),
        ...(loader ? { loader } : {}),
      };
    });

  const dashboardRoutes: RouteObject[] = Object.keys(
    DashboardNavigationLocations,
  )
    .filter((key) => isNaN(Number(key)))
    .map((key) => {
      const location = key as DashboardNavigationLocations;
      const route: RouteObject = {
        path: MapBrowserPath.mapToBrowserRelativePath(location),
        element: mapDashboardNavigationLocationToComponent(location),
      };

      // /dashboard/home: redirect to the role's dedicated dashboard if one exists,
      // otherwise render DashboardPage ("not ready" fallback).
      if (location === DashboardNavigationLocations.DASHBOARD) {
        route.loader = () => {
          const target = getDefaultDashboardPath();
          if (!target) return null;
          const normalizedTarget = target.startsWith("/")
            ? target
            : `/${target}`;
          return redirect(normalizedTarget);
        };
      } else {
        const requiredKeys = getDashboardRouteFeatureKeys(location);
        const requiredRoleLabels = getDashboardRouteRoleLabels(location);
        const moduleGated = isKybModuleGatedRoute(location);
        if (requiredKeys?.length || requiredRoleLabels?.length || moduleGated) {
          const routeId = MapBrowserPath.mapToBrowserRelativePath(location);
          route.loader = () =>
            assertDashboardRouteAccess(requiredKeys, { routeId }) ??
            assertDashboardRouteRole(requiredRoleLabels, { routeId }) ??
            (moduleGated
              ? assertDashboardModuleAccess(
                  useKybModuleStore.getState().enabled,
                  { routeId },
                )
              : null);
        }
      }

      return route;
    });

  const routes: RouteObject[] = [
    {
      path: browserPaths.HOME,
      element: <PublicLayout />,
      errorElement: <RouteErrorPage />,
      children: [
        ...publicRoutes,
        ...(process.env.NODE_ENV !== "production"
          ? [
              {
                path: "test-crash",
                element: <TestCrashPage />,
              } as RouteObject,
              {
                path: "test-route-error",
                loader: () => {
                  throw new Error("Test route loader error");
                },
                element: <div />,
              } as RouteObject,
            ]
          : []),
        {
          path: browserPaths.ERROR.ROOT_RELATIVE,
          element: <PublicErrorLayout />,
          children: [
            {
              path: browserPaths.ERROR.NOT_FOUND_RELATIVE,
              element: <NotFoundPage />,
            },
          ],
        },
      ],
    },
    {
      path: browserPaths.DASHBOARD.ROOT,
      element: <DashboardLayout />,
      errorElement: <RouteErrorPage />,
      loader: () => (authGuard() ? null : redirect(browserPaths.HOME)),
      children: [
        ...dashboardRoutes,
        {
          path: browserPaths.DASHBOARD.ERROR.ROOT_RELATIVE,
          element: <DashboardErrorLayout />,
          children: [
            {
              path: browserPaths.DASHBOARD.ERROR.NOT_FOUND_RELATIVE,
              element: <NotFoundPage />,
            },
            {
              path: browserPaths.DASHBOARD.ERROR.UNAUTHORIZED_RELATIVE,
              element: <DashboardPermissionDeniedPage />,
            },
            {
              path: browserPaths.DASHBOARD.ERROR.MODULE_INACTIVE_RELATIVE,
              element: <ModuleInactivePage />,
            },
          ],
        },
      ],
    },

    // Catch-all: invalid paths throw 404 → RouteErrorPage shows NotFoundFallback
    {
      path: `${browserPaths.DASHBOARD.ROOT}/*`,
      loader: () => {
        throw new Response(null, { status: 404, statusText: "Not Found" });
      },
      errorElement: <RouteErrorPage />,
    },
    {
      path: "*",
      loader: () => {
        throw new Response(null, { status: 404, statusText: "Not Found" });
      },
      errorElement: <RouteErrorPage />,
    },
  ];

  const router = createHashRouter(routes);

  return <RouterProvider router={router} />;
};

export default Router;
